import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, PanResponder, Platform, Text, View, ScrollView, Pressable } from 'react-native';
import {
  industries,
  matchProfiles,
  compareDiscovery,
  maxCollaboratorRating,
  reputationFor,
  type Profile,
} from '@ventureroom/core';
import { useApp, useData, useMe, useTheme } from './provider';
import { Avatar, Button, Column, Copy, Empty, Input, Row, Segments, Sheet } from './components';
import { SkillGradient } from './skill-gradient';
import { MoneyRating } from './money-rating';
import { founderShareText, shareFounder } from './sharing';
import { swipeAction, swipePreview, type SwipeAction } from './swipe';

const fill = { position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0 };
const cues = {
  like: { label: 'NETWORK', symbol: '↗', color: '#25674D' },
  pass: { label: 'PASS', symbol: '×', color: '#A83D2D' },
  share: { label: 'SHARE', symbol: '↑', color: '#246484' },
};

export function SwipeDeckFrame({ children }: { children: React.ReactNode }) {
  return (
    <View
      testID="swipe-deck-frame"
      style={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: 680,
        alignSelf: 'center',
        position: 'relative',
      }}
    >
      {children}
    </View>
  );
}
export function ProfileFilterButton({
  onPress,
  label = 'Discovery filters',
}: {
  onPress: () => void;
  label?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => ({
        minHeight: 48,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 16,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderColor: '#ded5d9',
        opacity: pressed ? 0.8 : 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 9,
      })}
    >
      <View accessibilityElementsHidden style={{ gap: 4, alignItems: 'center' }}>
        {[20, 14, 8].map((width) => (
          <View
            key={width}
            style={{ width, height: 2, borderRadius: 2, backgroundColor: '#50334b' }}
          />
        ))}
      </View>
      <Text style={{ fontSize: 15, fontWeight: '700', color: '#50334b' }}>Filters</Text>
    </Pressable>
  );
}

function CardTexture({ strong = false }: { strong?: boolean }) {
  return (
    <View
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={{ ...fill, overflow: 'hidden' }}
    >
      <View
        style={{
          ...fill,
          top: 12,
          left: 12,
          right: 12,
          bottom: 12,
          borderRadius: 16,
          borderWidth: strong ? 2 : 1,
          borderColor: strong ? '#ffffff99' : '#ffffff44',
        }}
      />
      {[160, 260, 360].map((diameter) => (
        <View
          key={diameter}
          style={{
            position: 'absolute',
            width: diameter,
            height: diameter,
            top: -diameter / 2,
            right: -diameter / 3,
            borderRadius: 28,
            transform: [{ rotate: '35deg' }],
            borderWidth: 2,
            borderColor: strong ? '#ffffff66' : '#ffffff30',
          }}
        />
      ))}
      {[240, 360, 480].map((diameter) => (
        <View
          key={diameter}
          style={{
            position: 'absolute',
            width: diameter,
            height: diameter,
            bottom: -diameter / 2,
            left: -diameter / 3,
            borderRadius: diameter / 2,
            borderWidth: 2,
            borderColor: strong ? '#ffffff66' : '#ffffff30',
          }}
        />
      ))}
    </View>
  );
}

function FounderFace({
  person,
  compact = false,
  note,
}: {
  person: Profile;
  compact?: boolean;
  note?: string;
}) {
  const me = useMe();
  const skillset = Array.from(
    new Set([...(person.ai?.specialisms ?? []), ...person.skills, ...person.brings]),
  );
  const investor = person.investorType && person.investorType !== 'None';
  return (
    <View
      pointerEvents="none"
      style={{
        flex: 1,
        padding: compact ? 18 : 26,
        paddingTop: 76,
        justifyContent: 'space-between',
        gap: compact ? 8 : 14,
      }}
    >
      <Column gap={compact ? 4 : 8}>
        <Row between>
          <Text style={{ color: '#fff', fontSize: compact ? 23 : 30, fontWeight: '700', flex: 1 }}>
            {person.name}
          </Text>
          <View style={{ borderRadius: 20, backgroundColor: '#ffffff20', padding: 8 }}>
            <Text style={{ color: '#fff', fontWeight: '700' }}>
              {person.name
                .split(' ')
                .slice(0, 2)
                .map((n) => n[0])
                .join('')}
            </Text>
          </View>
        </Row>
        <Copy color="#fff" size={11}>
          {person.is_ai
            ? `AI Collaborator · ${person.ai?.availability ?? 'available'}`
            : `${person.location} · ${person.hours} hrs/week`}
        </Copy>
        <MoneyRating person={person} light />
      </Column>
      <Column gap={compact ? 6 : 12}>
        <Copy color="#ffffffb0" size={10} style={{ letterSpacing: 2 }}>
          WHAT I BRING
        </Copy>
        <Text
          numberOfLines={2}
          style={{
            color: '#fff',
            fontSize: compact ? 25 : 35,
            fontWeight: '800',
            letterSpacing: -1,
          }}
        >
          {investor
            ? person.investorType
            : person.ai?.profession || skillset[0] || 'Build something together'}
        </Text>
        <Row wrap style={{ gap: 7 }}>
          {skillset.slice(0, compact ? 4 : 6).map((skill, i) => (
            <View
              key={skill}
              style={{
                backgroundColor: i === 0 ? '#ffffff' : '#ffffff1f',
                borderWidth: 1,
                borderColor: '#ffffff35',
                borderRadius: 14,
                paddingHorizontal: 12,
                paddingVertical: compact ? 6 : 9,
              }}
            >
              <Copy size={compact ? 12 : 14} bold color={i === 0 ? '#59334f' : '#fff'}>
                {skill}
              </Copy>
            </View>
          ))}
        </Row>
        {skillset.length > 6 && !compact && (
          <Copy size={11} color="#ffffffb0">
            +{skillset.length - 6} more strengths on profile
          </Copy>
        )}
        {!!note && (
          <View
            testID="request-note"
            style={{
              borderLeftWidth: 3,
              borderLeftColor: '#fff',
              backgroundColor: '#ffffff20',
              padding: 12,
              borderRadius: 8,
              gap: 6,
            }}
          >
            <Copy color="#fff" bold size={11}>
              COLLABORATION REQUEST
            </Copy>
            <Copy color="#fff" size={compact ? 14 : 17} lines={compact ? 3 : 5}>
              {note}
            </Copy>
          </View>
        )}
        {investor && (
          <Copy bold color="#fff" size={compact ? 14 : 18}>
            £{((person.investmentBudgetMinor ?? 0) / 100).toLocaleString('en-GB')} indicative
            investment · not committed
          </Copy>
        )}
      </Column>
      <Column gap={6}>
        {!compact && (
          <Copy color="#fff" size={14} lines={2}>
            {person.bio}
          </Copy>
        )}
        <Copy color="#ffffffc9" size={12} lines={compact ? 1 : 2}>
          {matchProfiles(me, person).reasons[0]}
        </Copy>
        <Copy color="#ffffffb0" size={10}>
          {note ? '← Decline · Accept → · Profile ↑' : '← Pass · Network → · Share ↑'}
        </Copy>
      </Column>
    </View>
  );
}
function VentureStrip({ person }: { person: Profile }) {
  const app = useApp();
  const ventures = person.ventureSummaries ?? [];
  if (!ventures.length)
    return (
      <View style={{ paddingHorizontal: 24, paddingBottom: 18 }}>
        <Copy color="#ffffffbb" size={11}>
          Ready for a new venture
        </Copy>
      </View>
    );
  return (
    <View style={{ paddingBottom: 16, gap: 6 }}>
      <Copy color="#ffffffbb" size={10} style={{ paddingHorizontal: 24, letterSpacing: 1 }}>
        VENTURES · {ventures.length}
      </Copy>
      <ScrollView
        showsHorizontalScrollIndicator={false}
        style={{ flexGrow: 0, maxHeight: 96 }}
        contentContainerStyle={{
          paddingHorizontal: 24,
          gap: 8,
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        {ventures.map((v) => (
          <Pressable
            key={v.id}
            accessibilityRole="button"
            accessibilityLabel={`Preview venture ${v.name}`}
            onPress={() => app.navigate(`venturepreview/${v.id}`)}
            style={{
              minHeight: 44,
              justifyContent: 'center',
              borderRadius: 14,
              backgroundColor: '#ffffff23',
              paddingHorizontal: 14,
              maxWidth: 220,
              flexShrink: 1,
            }}
          >
            <Copy color="#fff" size={12} bold lines={1}>
              {v.name} ↗
            </Copy>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
}

export function SwipeCard({
  person,
  onAction,
  onFilters,
  note,
  review = false,
}: {
  person: Profile;
  onAction: (action: SwipeAction) => Promise<boolean>;
  onFilters?: () => void;
  note?: string;
  review?: boolean;
}) {
  const t = useTheme();
  const app = useApp();
  const [position] = useState(() => new Animated.ValueXY());
  const [tints] = useState(() => ({
    like: new Animated.Value(0),
    pass: new Animated.Value(0),
    share: new Animated.Value(0),
  }));
  const size = useRef({ width: 380, height: 720 });
  const [height, setHeight] = useState(700);
  const locked = useRef(false);
  const dragged = useRef(false);
  const mounted = useRef(true);
  const [preview, setPreview] = useState<SwipeAction | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function openProfile() {
    if (!locked.current && !app.busy) app.navigate(`person/${person.id}`);
  }
  function feedback(dx: number, dy: number) {
    const next = swipePreview(dx, dy, size.current.width);
    for (const action of ['like', 'pass', 'share'] as const)
      tints[action].setValue(next.action === action ? next.progress : 0);
    setPreview(next.action);
  }
  function reset() {
    feedback(0, 0);
    Animated.spring(position, {
      toValue: { x: 0, y: 0 },
      useNativeDriver: false,
      speed: 24,
      bounciness: 5,
    }).start();
  }
  async function perform(action: SwipeAction) {
    if (locked.current || app.busy) return;
    locked.current = true;
    try {
      if (action === 'share') {
        // Keep browser user activation intact when opening the share sheet.
        const result = onAction(action);
        reset();
        await result;
      } else {
        const x = (action === 'like' ? 1 : -1) * (size.current.width * 1.5 + 100);
        feedback(action === 'like' ? size.current.width : -size.current.width, 0);
        await new Promise<void>((resolve) =>
          Animated.timing(position, {
            toValue: { x, y: 30 },
            duration: app.reduceMotion ? 0 : 280,
            useNativeDriver: false,
          }).start(() => resolve()),
        );
        const ok = await onAction(action);
        if (!ok && mounted.current) reset();
      }
    } finally {
      locked.current = false;
    }
  }
  // Keep one responder for the entire drag; crossing the centre changes only the preview.
  const latest = useRef({ app, perform, reset, feedback, openProfile });
  useEffect(() => {
    latest.current = { app, perform, reset, feedback, openProfile };
  });
  const [responder] = useState(() =>
    PanResponder.create({
      onStartShouldSetPanResponder: () => !locked.current && !latest.current.app.busy,
      onMoveShouldSetPanResponder: (_, g) =>
        !locked.current &&
        !latest.current.app.busy &&
        g.numberActiveTouches === 1 &&
        (Math.abs(g.dx) > 4 || Math.abs(g.dy) > 4),
      onPanResponderGrant: () => {
        dragged.current = false;
        position.stopAnimation();
      },
      onPanResponderMove: (_, g) => {
        if (Math.abs(g.dx) > 4 || Math.abs(g.dy) > 4) dragged.current = true;
        position.setValue({ x: g.dx, y: g.dy });
        latest.current.feedback(g.dx, g.dy);
      },
      onPanResponderRelease: (_, g) => {
        if (!dragged.current) {
          latest.current.openProfile();
          return;
        }
        const action = swipeAction(g.dx, g.dy, size.current.width);
        if (action) void latest.current.perform(action);
        else latest.current.reset();
      },
      onPanResponderTerminationRequest: () => false,
      onPanResponderTerminate: () => latest.current.reset(),
    }),
  );
  const webKeys =
    Platform.OS === 'web'
      ? {
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openProfile();
              return;
            }
            const action = ({ ArrowLeft: 'pass', ArrowRight: 'like', ArrowUp: 'share' } as const)[
              event.key as 'ArrowLeft'
            ];
            if (action) {
              event.preventDefault();
              void perform(action);
            }
          },
        }
      : {};
  return (
    <Animated.View
      testID="discovery-card"
      onLayout={(event) => {
        size.current = event.nativeEvent.layout;
        setHeight(event.nativeEvent.layout.height);
      }}
      style={{
        ...fill,
        overflow: 'hidden',
        borderRadius: 24,
        backgroundColor: t.avatars[person.avatar],
        transform: [
          { translateX: position.x },
          { translateY: position.y },
          {
            rotate: position.x.interpolate({
              inputRange: [-400, 0, 400],
              outputRange: ['-13deg', '0deg', '13deg'],
            }),
          },
        ],
        ...(Platform.OS === 'web' ? ({ userSelect: 'none' } as object) : {}),
      }}
    >
      <SkillGradient pointerEvents="none" style={fill} />
      <CardTexture />
      <View
        {...responder.panHandlers}
        {...webKeys}
        accessible
        focusable
        tabIndex={0}
        accessibilityRole="button"
        accessibilityLabel={`View ${person.name}'s profile`}
        accessibilityHint={
          review
            ? 'Swipe right to accept, left to decline, up to view profile. Release to confirm. On a keyboard use the arrow keys.'
            : 'Swipe right to network, left to skip, up to share. Release to confirm. On a keyboard use the arrow keys.'
        }
        accessibilityActions={[
          { name: 'activate', label: 'View profile' },
          { name: 'like', label: review ? 'Accept request' : 'Add to network' },
          { name: 'pass', label: review ? 'Decline request' : 'Skip founder' },
          { name: 'share', label: review ? 'View profile' : 'Share founder' },
        ]}
        onAccessibilityAction={(event) => {
          const action = event.nativeEvent.actionName;
          if (action === 'activate') openProfile();
          else if (action in cues) void perform(action as SwipeAction);
        }}
        style={{ flex: 1, ...(Platform.OS === 'web' ? ({ touchAction: 'none' } as object) : {}) }}
      >
        <FounderFace person={person} compact={height < 540} note={note} />
      </View>
      <VentureStrip person={person} />
      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          top: 12,
          left: 24,
          right: 12,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text
          pointerEvents="none"
          style={{ color: '#fff', fontSize: 27, fontWeight: '800', letterSpacing: -2 }}
        >
          v.
        </Text>
        {onFilters && (
          <ProfileFilterButton
            onPress={onFilters}
            label={review ? 'Team request filters' : 'Discovery filters'}
          />
        )}
      </View>
      {(['like', 'pass', 'share'] as const).map((action) => (
        <Animated.View
          key={action}
          pointerEvents="none"
          testID={`swipe-${action}-feedback`}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={{
            ...fill,
            opacity: tints[action],
            borderWidth: 4,
            borderRadius: 24,
            borderColor: cues[action].color,
          }}
        >
          <View
            testID={`swipe-${action}-fill`}
            style={{ ...fill, backgroundColor: cues[action].color }}
          />
          <CardTexture strong />
          <View style={{ ...fill, justifyContent: 'center', alignItems: 'center' }}>
            <Text style={{ fontSize: 50, fontWeight: '800', color: '#fff' }}>
              {cues[action].symbol}
            </Text>
            <Text
              style={{
                color: '#fff',
                fontSize: 26,
                fontWeight: '800',
                letterSpacing: 3,
              }}
            >
              {review
                ? { like: 'ACCEPT', pass: 'DECLINE', share: 'PROFILE' }[action]
                : cues[action].label}
            </Text>
          </View>
        </Animated.View>
      ))}
      <View
        accessibilityLiveRegion="polite"
        accessibilityLabel={preview ? cues[preview].label : undefined}
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
      />
    </Animated.View>
  );
}

export function Discover() {
  const app = useApp();
  const data = useData();
  const me = useMe();
  const t = useTheme();
  const { search, industry, role } = app.discoveryFilters;
  const setSearch = (search: string) =>
    app.setDiscoveryFilters({ ...app.discoveryFilters, search });
  const setIndustry = (industry: string) =>
    app.setDiscoveryFilters({ ...app.discoveryFilters, industry });
  const setRole = (role: string) => app.setDiscoveryFilters({ ...app.discoveryFilters, role });
  const [filters, setFilters] = useState(false);
  const [matched, setMatched] = useState<Profile | null>(null);
  const [shareText, setShareText] = useState<string | null>(null);
  const people = useMemo(
    () =>
      data.profiles
        .filter(
          (p) =>
            p.id !== me.id &&
            p.looking &&
            (p.reputation?.money ?? reputationFor(data, p).money) <= maxCollaboratorRating(me) &&
            (role === 'Anyone' ||
              p.role === role ||
              (role === 'Business Owner' && p.role === 'Both') ||
              (role === 'Skill Provider' && p.role === 'Both')) &&
            !data.bookmarks.some(
              (b) => b.userId === me.id && b.personId === p.id && b.state === 'passed',
            ) &&
            !data.connections.some(
              (c) =>
                [c.from, c.to].includes(me.id) &&
                [c.from, c.to].includes(p.id) &&
                (c.status === 'accepted' || c.from === me.id),
            ) &&
            (industry === 'All interests' || p.industries.includes(industry)) &&
            `${p.name} ${p.skills.join(' ')} ${p.ai?.specialisms.join(' ') ?? ''} ${p.ai?.profession ?? ''} ${p.investorType ?? ''} ${p.bio}`
              .toLowerCase()
              .includes(search.toLowerCase()),
        )
        .sort((a, b) => compareDiscovery(me, a, b)),
    [data, me, industry, search, role],
  );
  const person = people[0];
  async function choose(action: SwipeAction): Promise<boolean> {
    if (!person) return false;
    if (action === 'share') {
      try {
        const result = await shareFounder(person);
        if (result === 'unavailable') setShareText(founderShareText(person));
      } catch {
        setShareText(founderShareText(person));
      }
      return true;
    }
    if (action === 'pass')
      return app.act({ type: 'bookmark', personId: person.id, state: 'passed' }, 'Passed profile');
    const mutual = data.connections.some(
      (c) => c.from === person.id && c.to === me.id && c.status === 'pending',
    );
    const ok = await app.act({ type: 'connect', personId: person.id, intro: '' }, '');
    if (ok && mutual) setMatched(person);
    return ok;
  }
  return (
    <View
      testID="discovery-deck"
      style={{
        flex: 1,
        minHeight: 0,
        backgroundColor: t.background,
        padding: 6,
        overflow: 'hidden',
      }}
    >
      <SwipeDeckFrame>
        <View style={{ flex: 1, minHeight: 0 }}>
          {!!people[1] && (
            <View
              importantForAccessibility="no-hide-descendants"
              accessibilityElementsHidden
              pointerEvents="none"
              style={{
                ...fill,
                borderRadius: 24,
                overflow: 'hidden',
                backgroundColor: t.avatars[people[1].avatar],
              }}
            >
              <SkillGradient pointerEvents="none" style={fill} />
            </View>
          )}
          {person ? (
            <SwipeCard
              key={person.id}
              person={person}
              onAction={choose}
              onFilters={() => setFilters(true)}
            />
          ) : null}
        </View>

        {!person && (
          <View style={{ flex: 1, justifyContent: 'center', padding: 28 }}>
            <Empty
              title="You’re caught up"
              detail={
                search || industry !== 'All interests'
                  ? 'Try another filter.'
                  : 'Your connections are in Chats.'
              }
              action={
                <Button
                  kind="quiet"
                  onPress={() =>
                    search || industry !== 'All interests'
                      ? app.setDiscoveryFilters({
                          ...app.discoveryFilters,
                          search: '',
                          industry: 'All interests',
                        })
                      : app.navigate('connections')
                  }
                >
                  {search || industry !== 'All interests' ? 'Clear filters' : 'Open chats'}
                </Button>
              }
            />
            <Button kind="quiet" label="Discovery filters" onPress={() => setFilters(true)}>
              Filters
            </Button>
          </View>
        )}
        {!!app.error && (
          <View
            accessibilityRole="alert"
            style={{
              position: 'absolute',
              bottom: 12,
              left: 20,
              right: 20,
              padding: 12,
              backgroundColor: t.background,
              borderRadius: 8,
            }}
          >
            <Copy color={t.danger}>{app.error}</Copy>
          </View>
        )}
      </SwipeDeckFrame>
      {filters && (
        <Sheet title="Filters" onClose={() => setFilters(false)}>
          <Copy muted size={12}>
            Your plan includes collaborators up to {maxCollaboratorRating(me)}/5 money bags. Paid
            plans unlock 4; Enterprise and Ultimate unlock 5.
          </Copy>
          <Button
            kind="quiet"
            onPress={() => {
              setFilters(false);
              app.navigate('settings');
            }}
          >
            View plans
          </Button>
          <Input
            label="Find a strength or a person"
            value={search}
            onChange={setSearch}
            placeholder="Name or skill"
          />
          <Segments
            items={['Anyone', 'Skill Provider', 'Business Owner']}
            value={role}
            onChange={setRole}
          />
          <Segments
            items={['All interests', ...industries]}
            value={industry}
            onChange={setIndustry}
          />
          <Button onPress={() => setFilters(false)}>Show results</Button>
        </Sheet>
      )}
      {matched && (
        <Sheet title="It’s a match!" onClose={() => setMatched(null)}>
          <Row style={{ justifyContent: 'center' }}>
            <Avatar person={me} size={80} />
            <Copy size={28} color={t.accent}>
              ↗
            </Copy>
            <Avatar person={matched} size={80} />
          </Row>
          <Copy>You and {matched.name.split(' ')[0]} are connected.</Copy>
          <Button
            onPress={() => {
              setMatched(null);
              app.navigate(`chat/${matched.id}`);
            }}
          >
            Start chatting
          </Button>
          <Button kind="quiet" onPress={() => setMatched(null)}>
            Keep discovering
          </Button>
        </Sheet>
      )}
      {shareText && (
        <Sheet title="Share founder" onClose={() => setShareText(null)}>
          <Text selectable style={{ color: t.text, fontSize: 15, lineHeight: 23 }}>
            {shareText}
          </Text>
        </Sheet>
      )}
    </View>
  );
}
